// Conversation d'une demande avec le service (messagerie du socle, H8-01, R15) : résumés affichés
// sur la fiche et dans la liste des messages, messages à signaler comme nouveaux, nom de l'auteur,
// message lisible d'un envoi refusé. Aucun accès réseau ici : fonctions pures, testées seules.
import { ErreurApi, messageErreur } from '../../api/erreurs';
import { date, pluriel } from '../../commun/format';
import type { ConversationDossier, MessageConversationDossier } from './types';

/** Nombre de pièces par message accepté par l'API (schéma d'envoi de la messagerie). */
export const MAX_PIECES_MESSAGE = 10;

/** Longueur maximale d'un message (schéma d'envoi de la messagerie). */
export const LONGUEUR_MAX_MESSAGE = 10_000;

export function dernierMessage(c: ConversationDossier): MessageConversationDossier | null {
  return c.messages[c.messages.length - 1] ?? null;
}

/** « 1 non lu », « 3 non lus ». */
export function libelleNonLus(n: number): string {
  return pluriel(n, 'non lu');
}

/** Sous-titre d'une ligne « Messages avec le service » : état réel de la conversation, jamais inventé. */
export function resumeConversation(c: ConversationDossier): string {
  if (c.statut === 'aucune') return 'Pas encore de conversation : le service l\'ouvrira en vous écrivant';
  const dernier = dernierMessage(c);
  if (!dernier) return c.statut === 'archivee' ? 'Aucun message, conversation archivée' : 'Aucun message pour l\'instant';
  const base = `${pluriel(c.messages.length, 'message')}, dernier le ${date(dernier.horodatage)}`;
  return c.statut === 'archivee' ? `${base}, conversation archivée` : base;
}

/**
 * Messages à signaler comme nouveaux après une lecture : le serveur compte dans « non_lus » les
 * messages d'autrui postérieurs à la dernière lecture, ce sont donc les n derniers messages qui ne
 * sont pas du compte connecté (ou, à défaut d'identifiant, qui viennent de la collectivité).
 * « nonLusAvant » est le nombre que la fiche donnait avant l'ouverture de l'écran : si une lecture
 * précédente a déjà marqué lu (double requête, autre onglet), ces messages restent signalés.
 */
export function nouveauxMessages(c: ConversationDossier, moi: string | null, nonLusAvant = 0): string[] {
  const n = Math.max(c.non_lus, nonLusAvant);
  if (n <= 0) return [];
  const autres = c.messages.filter((m) => (moi ? m.auteur.compte_id !== moi : m.auteur.cote !== 'usager'));
  return autres.slice(-n).map((m) => m.id);
}

/** « Vous » pour le compte connecté ; le nom servi par l'API sinon (agent du service ou autre usager, par exemple un mandataire). */
export function nomAuteur(m: MessageConversationDossier, moi: string | null): string {
  if (moi && m.auteur.compte_id === moi) return 'Vous';
  if (m.auteur.cote === 'collectivite') return m.auteur.nom ? `Le service · ${m.auteur.nom}` : 'Le service';
  return m.auteur.nom ?? 'Un usager';
}

interface Refus {
  titre?: string;
  motif?: string;
  participants?: { nom?: string; motif?: string }[];
}

function refusLisibles(details: unknown): string[] {
  const liste = (details as { refus?: unknown } | null)?.refus;
  if (!Array.isArray(liste)) return [];
  return liste
    .map((r) => {
      const x = r as Refus;
      const motif = x.motif ?? (x.participants ?? []).map((p) => p.motif).filter(Boolean).join(', ');
      return [x.titre, motif].filter(Boolean).join(' : ');
    })
    .filter((t) => t.length > 0);
}

/** Message d'erreur d'un envoi, selon le code renvoyé par l'API de la conversation. */
export function messageErreurEnvoi(e: unknown): string {
  if (e instanceof ErreurApi) {
    if (e.code === 'conversation_archivee') return 'La conversation est archivée : la demande est terminée, plus aucun message ne peut être envoyé.';
    if (e.code === 'conversation_absente') return 'La conversation de cette demande n\'est pas encore ouverte : le service l\'ouvrira en vous écrivant.';
    if (e.code === 'piece_non_autorisee') {
      const refus = refusLisibles(e.details);
      return refus.length > 0 ? `Pièce refusée : ${refus.join(' ; ')}.` : `Pièce refusée : ${e.message}`;
    }
  }
  return messageErreur(e);
}
